// This file is code for the main area of the page that shows the form.  After
// importing this file, the initFormPane() function should be called.

import lformsUpdater from 'lforms-updater';
import { Dialogs } from './dialogs';
import { config } from './config';
import { announce } from './announcer'; // for a screen reader
import {spinner} from '../spinner.js';

/**
 *  A reference to the element for showing error messages.
 */
const errMsgElem_ = document.getElementById('errMsg');

/**
 *  A reference to the element containing the initial instructions which are
 *  removed once a form loads.
 */
const initialMsgElem_ = document.getElementById('initialMsg');

/**
 *  A reference the "save" button.
 */
const saveBtn_ = document.getElementById('saveBtn');

/**
 *  A reference to the group containing the save & load buttons.
 */
const loadSaveGroup_ = saveBtn_.parentNode;

/**
 *  A reference to the "show form data as" menu.
 */
const formDataMenu_ = document.getElementById('formDataMenu');

/**
 *  A reference to the element into which the form will be placed.
 */
const formContainer_ = document.getElementById(config.formContainer);

/**
 *  A reference to the last Object URL created for "save to file".
 */
var saveToFileObjURL_ = null;


/**
 *  Renders the given form definition, replacing any previously shown form.
 * @param formDef either an LForms or FHIR Questionnaire form definition.
 */
export function showForm(formDef) {
  hide(initialMsgElem_);
  removeErrMsg();
  removeForm();
  spinner.show();
  const showRepetitionNumbers = document.getElementById('showRepetitionNumbers').checked;
  const showIndentation = document.getElementById('showIndentation').checked;
  const showTreeLines = document.getElementById('showTreeLines').checked;
  LForms.Util.addFormToPage(formDef, formContainer_, {
    allowHTML: true,
    hideRepetitionNumber: !showRepetitionNumbers,
    hideIndentation: !showIndentation,
    hideTreeLine: !showTreeLines
  }).then(() => {
    spinner.hide();
    //show(saveBtn_);
    loadSaveGroup_.appendChild(saveBtn_);
    show(formDataMenu_, 'inline-block');
    announce('A form is now displayed in the main content area, '+
      ' along with a save button and a menu for showing the form data.');
  }, (error)=>{
    console.log(error);
    showError('Could not display the form.', error);
  }).finally(()=> {
    spinner.hide();
  });
}


/**
 *  Removes the error message displayed (if any).
 */
function removeErrMsg() {
  hide(errMsgElem_);
  errMsgElem_.textContent = '';
}

/**
 *  Removes the form displayed (if any)
 */
function removeForm() {
  formContainer_.textContent = '';
}


/**
 *  Hides an element.
 * @param elem the element to hide.
 */
function hide(elem) {
  elem.style.display = 'none';
}


/**
 *  Shows a element (by setting display to block, unless the second parameter is
 *  provided.
 * @param elem the element to show.
 * @param displaySetting (optional, default 'block') the setting for 'display'
 */
function show(elem, displaySetting) {
  displaySetting ||= 'block';
  elem.style.display = displaySetting;
}


/**
 *  Removes any visible form and shows the given error message.
 * @param msg the error message to show
 * @param error (optional) an Error that was thrown to report the problem.
 */
export function showError(msg, error) {
  removeForm();
  errMsgElem_.textContent = msg;
  announce(msg);
  if (error) {
    console.log(error);
    const details = document.createElement("div");
    const detailMsg = 'Cause: ' + error.toString();
    details.textContent = detailMsg;
    errMsgElem_.appendChild(details);
    announce(detailMsg);
  }
  show(errMsgElem_);
  //hide(saveBtn_);
  loadSaveGroup_.removeChild(saveBtn_);
  hide(formDataMenu_);
}

// Set up the theme list.
// Copying themes.js ("themeList") from lforms to avoid having lforms as an npm dependency
const themeList = {
  defaultValue: "Default",
  listItems: [
    {text: "Default"},
    {text: "Green", code: "green"},
    {text: "MC", code: "mc"},
    {text: "Cyan", code: "cyan"},
    {text: "Teal", code: "teal"},
    {text: "Coffee", code: "coffee"},
    {text: "Spring", code: "spring"},
    {text: "Summer", code: "summer"},
    {text: "Fall", code: "fall"},
    {text: "Winter", code: "winter"}
  ]
};
const themeCodes = [];
const themeDisplay = [];
const themeDefault = themeList.defaultValue;
themeList.listItems.forEach(item => {
  themeCodes.push(item.code);
  themeDisplay.push(item.text);
});

/**
 *  Initializes the form pane.
 */
export function initFormPane() {
  const themeAC = new LForms.Def.Autocompleter.Prefetch('theme', themeDisplay,
    {codes: themeCodes, defaultValue: themeDefault});
  themeAC.setFieldToListValue(themeDefault);
  LForms.Def.Autocompleter.Event.observeListSelections('theme', (itemData)=>{
    const itemCode = itemData.item_code;
    document.body.className = itemCode ? 'theme-'+itemCode : '';
  });

  // Set up the load file button.
  document.getElementById('loadBtn').addEventListener('click', ()=>{
    document.getElementById('loadFileInput').click();
  });
  const loadFileInput = document.getElementById('loadFileInput');
  loadFileInput.addEventListener('change', ()=>{
    const files = loadFileInput.files;
    if (files.length > 0) {
      var reader = new FileReader(); // Read from local file system.
      reader.onload = function(event) {
        try {
          var importedData = JSON.parse(event.target.result);
          // Update it to the current version of LForms
          importedData = lformsUpdater.update(importedData);
          showForm(importedData);
        }
        catch (e) {
          showError('Could not process the file.', e);
        }
      };
      reader.readAsText(files[0]);
    }
    loadFileInput.value = ''; // so the same file can be selectd again
  });

  // Set up the "save to file" button
  document.getElementById('saveBtn').addEventListener('click', ()=>{
    const formData = LForms.Util.getFormData(formContainer_);
    var formDataStr = JSON.stringify(formData);
    var blob = new Blob([formDataStr], {type: 'application/json;charset=utf-8'});
    var exportFileName = formData.name + '[' + formData.code + ']';

    // Use hidden anchor to do file download.
    //var downloadLink = document.getElementById('exportAnchor');
    const downloadLink = document.createElement('a');
    var urlFactory = (window.URL || window.webkitURL);
    if(saveToFileObjURL_ != null) {
      // First release any resources on existing object url
      urlFactory.revokeObjectURL(saveToFileObjURL_);
    }
    saveToFileObjURL_ = urlFactory.createObjectURL(blob);
    downloadLink.setAttribute('href', saveToFileObjURL_);
    downloadLink.setAttribute('download', exportFileName + '.json');
    downloadLink.click();
  });

  // Hide the save button.  We remove it for the sake of CSS styling on the button
  // group, which has rounded edges around the group, but not between buttons
  // (even if one is hidden).
  loadSaveGroup_.removeChild(saveBtn_);

  // Show or hide repetition numbers for the form displayed on the page, if there is one already displayed.
  document.getElementById('showRepetitionNumbers').addEventListener('change', (event) => {
    const formTag = document.getElementsByTagName('wc-lhc-form')[0];
    if (formTag) {
      // Use spread syntax so we get a new object while maintaining untouched properties like allowHTML.
      formTag.options = {...formTag.options, hideRepetitionNumber: !event.target.checked};
    }
  });
  // Show or hide indentations for the form displayed on the page, if there is one already displayed.
  document.getElementById('showIndentation').addEventListener('change', (event) => {
    const formTag = document.getElementsByTagName('wc-lhc-form')[0];
    if (formTag) {
      // Use spread syntax so we get a new object while maintaining untouched properties like allowHTML.
      formTag.options = {...formTag.options, hideIndentation: !event.target.checked};
    }
    // Hide "show tree lines" checkbox if "show indentation" is unchecked. Tree lines won't be shown.
    document.getElementById('showTreeLinesContainer').style.display = event.target.checked ? 'inline' : 'none';
  });
  // Show or hide tree lines for the form displayed on the page, if there is one already displayed.
  document.getElementById('showTreeLines').addEventListener('change', (event) => {
    const formTag = document.getElementsByTagName('wc-lhc-form')[0];
    if (formTag) {
      // Use spread syntax so we get a new object while maintaining untouched properties like allowHTML.
      formTag.options = {...formTag.options, hideTreeLine: !event.target.checked};
    }
  });

  // Set up the dialogs for the "show data as" menu.
  Dialogs.init(formContainer_);
}
